import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { notificationsApi } from '@/utils/api';
import type { Notification, NotificationSummary, NotificationPreferences, NotificationType } from '@/types';

interface NotificationsState {
  notifications: Notification[];
  summary: NotificationSummary | null;
  preferences: NotificationPreferences | null;
  isLoading: boolean;
  error: string | null;
  unreadCount: number;
  hasMore: boolean;
  page: number;
}

const initialState: NotificationsState = {
  notifications: [],
  summary: null,
  preferences: null,
  isLoading: false,
  error: null,
  unreadCount: 0,
  hasMore: true,
  page: 1,
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params?: { unreadOnly?: boolean; page?: number; limit?: number }) => {
    const response = await notificationsApi.getAll(params);
    return response.data;
  }
);

export const fetchNotificationSummary = createAsyncThunk(
  'notifications/fetchSummary',
  async () => {
    const response = await notificationsApi.getSummary();
    return response.data;
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string) => {
    await notificationsApi.markAsRead(notificationId);
    return notificationId;
  }
);

export const markNotificationAsUnread = createAsyncThunk(
  'notifications/markAsUnread',
  async (notificationId: string) => {
    await notificationsApi.markAsUnread(notificationId);
    return notificationId;
  }
);

export const bulkMarkAsRead = createAsyncThunk(
  'notifications/bulkMarkAsRead',
  async (notificationIds: string[]) => {
    await notificationsApi.bulkMarkAsRead(notificationIds);
    return notificationIds;
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (notificationId: string) => {
    await notificationsApi.delete(notificationId);
    return notificationId;
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async () => {
    const response = await notificationsApi.getUnreadCount();
    return response.data.count;
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetNotifications: (state) => {
      state.notifications = [];
      state.page = 1;
      state.hasMore = true;
    },
    updatePreferences: (state, action: PayloadAction<Partial<NotificationPreferences>>) => {
      if (state.preferences) {
        state.preferences = { ...state.preferences, ...action.payload };
      }
    },
    ensureSummaryInitialized: (state) => {
      if (!state.summary) {
        state.summary = {
          total: 0,
          unread: 0,
          read: 0,
          byType: {
            INFO: 0,
            WARNING: 0,
            ERROR: 0,
            SUCCESS: 0,
          },
        };
      } else {
        state.summary = {
          total: state.summary.total ?? 0,
          unread: state.summary.unread ?? 0,
          read: state.summary.read ?? 0,
          byType: {
            INFO: state.summary.byType?.INFO ?? 0,
            WARNING: state.summary.byType?.WARNING ?? 0,
            ERROR: state.summary.byType?.ERROR ?? 0,
            SUCCESS: state.summary.byType?.SUCCESS ?? 0,
          },
        };
      }
    },
    // Real-time notification updates (for WebSocket integration)
    addNewNotification: (state, action: PayloadAction<Notification>) => {
      notificationsSlice.caseReducers.ensureSummaryInitialized(state);
      state.notifications.unshift(action.payload);
      state.unreadCount += 1;
      notificationsSlice.caseReducers.ensureSummaryInitialized(state);
      state.summary!.total += 1;
      state.summary!.unread += 1;
      state.summary!.byType[action.payload.type] = (state.summary!.byType[action.payload.type] || 0) + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { data?: Notification[]; meta?: { unreadCount?: number } } | undefined;
        const notifications = payload?.data || [];
        const meta = payload?.meta || {};
        const pageArg = (action.meta?.arg as { page?: number })?.page ?? 1;
        const limitArg = (action.meta?.arg as { limit?: number })?.limit ?? 50;
        
        if (pageArg === 1) {
          state.notifications = notifications;
        } else {
          state.notifications.push(...notifications);
        }
        
        state.unreadCount = meta?.unreadCount || 0;
        state.hasMore = notifications.length === limitArg;
        state.page = pageArg + 1;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      })

      // Fetch summary
      .addCase(fetchNotificationSummary.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotificationSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { data?: { total?: number; unread?: number; read?: number; byType?: Record<string, number> } } | undefined;
        const payloadSummary = payload?.data || {};
        state.summary = {
          total: payloadSummary.total ?? 0,
          unread: payloadSummary.unread ?? 0,
          read: payloadSummary.read ?? 0,
          byType: {
            INFO: 0,
            WARNING: 0,
            ERROR: 0,
            SUCCESS: 0,
            ...(payloadSummary.byType || {}),
          },
        };
        state.unreadCount = state.summary.unread;
      })
      .addCase(fetchNotificationSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch notification summary';
      })

      // Mark as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        notificationsSlice.caseReducers.ensureSummaryInitialized(state);
        const notificationId = action.payload;
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          
          state.summary!.unread = Math.max(0, state.summary!.unread - 1);
          state.summary!.read += 1;
          state.summary!.byType[notification.type] = Math.max(0, (state.summary!.byType[notification.type] || 0) - 1);
        }
      })

      // Mark as unread
      .addCase(markNotificationAsUnread.fulfilled, (state, action) => {
        notificationsSlice.caseReducers.ensureSummaryInitialized(state);
        const notificationId = action.payload;
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification && notification.isRead) {
          notification.isRead = false;
          notification.readAt = undefined;
          state.unreadCount += 1;
          
          state.summary!.unread += 1;
          state.summary!.read = Math.max(0, state.summary!.read - 1);
          state.summary!.byType[notification.type] = (state.summary!.byType[notification.type] || 0) + 1;
        }
      })

      // Bulk mark as read
      .addCase(bulkMarkAsRead.fulfilled, (state, action) => {
        notificationsSlice.caseReducers.ensureSummaryInitialized(state);
        const notificationIds = action.payload;
        let markedCount = 0;
        
        notificationIds.forEach(id => {
          const notification = state.notifications.find(n => n.id === id);
          if (notification && !notification.isRead) {
            notification.isRead = true;
            notification.readAt = new Date().toISOString();
            markedCount += 1;
          }
        });
        
        state.unreadCount = Math.max(0, state.unreadCount - markedCount);
        
        state.summary!.unread = Math.max(0, state.summary!.unread - markedCount);
        state.summary!.read += markedCount;
      })

      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        notificationsSlice.caseReducers.ensureSummaryInitialized(state);
        const notificationId = action.payload;
        const notificationIndex = state.notifications.findIndex(n => n.id === notificationId);
        
        if (notificationIndex !== -1) {
          const notification = state.notifications[notificationIndex];
          if (notification) {
            state.notifications.splice(notificationIndex, 1);

            if (!notification.isRead) {
              state.unreadCount = Math.max(0, state.unreadCount - 1);
            }

            state.summary!.total = Math.max(0, state.summary!.total - 1);
            if (!notification.isRead) {
              state.summary!.unread = Math.max(0, state.summary!.unread - 1);
              state.summary!.byType[notification.type] = Math.max(0, (state.summary!.byType[notification.type] || 0) - 1);
            } else {
              state.summary!.read = Math.max(0, state.summary!.read - 1);
            }
          }
        }
      })

      // Fetch unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      });
  },
});

export const { 
  clearError, 
  resetNotifications, 
  updatePreferences, 
  addNewNotification 
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
